import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  timeZone,
  tripTimeline,
  getTimeline,
  vehicleTripData,
  getVehicleTripData,
  isTimelineLoading,
} from "store/vehicleTripSlice";
import { getTrip, tripData, pointsOfService, getPointsOfService } from "store/tripSlice";
import { Box, Button, Stack, Tooltip } from "@mui/material";
import { Edit as EditIcon, Visibility as ViewIcon, Save as SaveIcon } from "@mui/icons-material";
import moment from "moment";
import orgDataService from "services/orgDataService";
import { toast } from "react-toastify";
import EventsService from "services/eventsService";
import tripService from "services/tripService";
import SuggestedEventsService from "services/suggestedEventsService";
import CloserEventsGrid from "./closer/CloserEventsGrid";
import VehicleHistory from "map/components/VehicleHistory";
import { AuthContext } from "common/AuthProvider";

interface MapEditLayoutProps {
  tripNumber?: string;
  isHidden?: boolean;
}

const MapEditLayout = ({ tripNumber, isHidden }: MapEditLayoutProps) => {
  const dispatch = useDispatch();
  const trip = useSelector(tripData);
  const { userHasPermission } = useContext(AuthContext);
  const [editMode, setEditMode] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Closer data management state (mirrors CloserLayout)
  const [isLoading, setIsLoading] = useState(true);
  const _tripTimeline = useSelector(tripTimeline);
  const _timeZone = useSelector(timeZone);
  const [waitReasons, setWaitReasons] = useState([]);
  const [cancelReasons, setCancelReasons] = useState([]);
  const [tripEvents, setTripEvents] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const _pointsOfService = useSelector(pointsOfService);
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]);
  const [showValidationErrors, setShowValidationErrors] = useState(true);
  const [expandedValidationErrors, setExpandedValidationErrors] = useState(true);
  const [approveLoading, setApproveLoading] = useState(false);
  const [gridCollapsed, setGridCollapsed] = useState(false);
  const [activeFilter, setActiveFilter] = useState<string>("active");
  const [canOverride, setCanOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [validationLoaded, setValidationLoaded] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [mapDataReady, setMapDataReady] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [managerReviewLoading, setManagerReviewLoading] = useState(false);
  const timelineLoading = useSelector(isTimelineLoading);
  const _vehicleActivity = useSelector(vehicleTripData);

  // Map <-> grid interaction
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);

  // Permissions
  const hasAllowApprove = userHasPermission("AllowApprove");
  const hasAllowOverride = userHasPermission("AllowLitmusOverride");
  const hasAllowClearAllEvents = userHasPermission("AllowClearAllEvents");

  // Accepted/dismissed stats
  const [acceptedWaitCount, setAcceptedWaitCount] = useState(0);
  const [acceptedWaitMinutes, setAcceptedWaitMinutes] = useState(0);
  const [dismissedWaitCount, setDismissedWaitCount] = useState(0);
  const [dismissedWaitMinutes, setDismissedWaitMinutes] = useState(0);

  // Load closer reference data when trip is available
  useEffect(() => {
    if (!trip) return;

    Promise.all([
      orgDataService.getWaitReasons(trip.clientId),
      orgDataService.getTripCancelReasons(),
      orgDataService.getHubsByClient(trip.clientId),
      orgDataService.getEventTypes(),
      dispatch(getPointsOfService({ clientId: trip.clientId, hubId: trip.hub?.id })),
    ]).then(([waits, cancels, hubs, events]) => {
      setWaitReasons(waits);
      setCancelReasons(cancels);
      setLocations(hubs);
      setEventTypes(events);
      setIsLoading(false);
    });
  }, [trip, dispatch]);

  // Sync tripEvents from timeline. Skip while there are unsaved local edits so
  // background reloads (e.g. activeFilter change) don't blow away the user's work.
  useEffect(() => {
    if (!_tripTimeline) return;
    if (hasUnsavedChanges) return;
    setTripEvents(_tripTimeline);
  }, [_tripTimeline, hasUnsavedChanges]);

  // The server-side timeline is already filtered to the current activeFilter, but our
  // local tripEvents always carries every event (including ones the user just toggled
  // inactive). Match the server filter when handing the override to VehicleHistory so
  // a "Remove" actually disappears from the activity cards.
  const visibleTripEvents = useMemo(() => {
    if (activeFilter === "inactive") return tripEvents.filter((e: any) => e.isActive === false);
    if (activeFilter === "all") return tripEvents;
    return tripEvents.filter((e: any) => e.isActive !== false);
  }, [tripEvents, activeFilter]);

  // Fetch timeline when trip or activeFilter changes
  const prevFilterRef = React.useRef(activeFilter);
  useEffect(() => {
    if (!trip?.id) return;
    if (prevFilterRef.current !== activeFilter) {
      setTripEvents([]);
      prevFilterRef.current = activeFilter;
    }
    dispatch(getTimeline({ tripNumber: trip.id.toString(), isActive: activeFilter }));
  }, [trip?.id, activeFilter, dispatch]);

  // Fetch accepted/dismissed stats
  useEffect(() => {
    if (!trip?.id) return;
    SuggestedEventsService.getTripStats(trip.id)
      .then((stats) => {
        setAcceptedWaitCount(stats.acceptedWaitCount);
        setAcceptedWaitMinutes(stats.acceptedWaitMinutes);
        setDismissedWaitCount(stats.dismissedWaitCount);
        setDismissedWaitMinutes(stats.dismissedWaitMinutes);
      })
      .catch(() => {});
  }, [trip?.id, tripEvents]);

  // Pre-fetch vehicle trip data for Apply GPS Data
  useEffect(() => {
    if (!trip?.vehicle?.unitNumber || !_tripTimeline?.length) return;

    const startEvent = _tripTimeline.find((t: any) => t.type === "Begin Trip");
    const endEvent = _tripTimeline.find((t: any) => t.type === "End Trip");
    if (!startEvent?.time) return;

    const start = moment.utc(startEvent.time);
    const end = endEvent?.time
      ? moment.utc(endEvent.time)
      : moment.utc(startEvent.time).add(12, "hours");

    dispatch(
      getVehicleTripData({
        unitNumber: +trip.vehicle.unitNumber,
        startDate: start,
        endDate: end,
        timezone: _timeZone,
        tripNumber: trip.tripNumber,
        clientName: trip.clientName,
      })
    );
  }, [trip?.vehicle?.unitNumber, _tripTimeline, _timeZone, dispatch]);

  // Map data readiness
  useEffect(() => {
    setMapDataReady(
      !!(_vehicleActivity?.activity?.length || _vehicleActivity?.telemetry?.length)
    );
  }, [_vehicleActivity]);

  // Validation
  const runValidation = useCallback(() => {
    if (!trip?.id) return Promise.resolve();

    setValidationLoaded(false);
    return tripService.validate(trip.id).then((response) => {
      if (response) {
        const allItems = response.errors || [];
        const errors = allItems.filter((e) => e.errorType !== "Warning");
        const warnings = allItems.filter((e) => e.errorType === "Warning");
        setValidationErrors(errors);
        setValidationWarnings(warnings);
        setShowValidationErrors(!response.isValid || warnings.length > 0);
        setExpandedValidationErrors(!response.isValid);
        setCanOverride(response.overrideValidation.canOverride);
        setOverrideReason(response.overrideValidation.reason || "");
        setValidationLoaded(true);
      }
    });
  }, [trip?.id]);

  useEffect(() => {
    runValidation();
  }, [runValidation]);

  const handleValidationResponse = (response, successMessage, errorPrefix) => {
    if (!response) {
      toast.success(successMessage);
      return;
    }
    const allItems = response.errors || [];
    const errors = allItems.filter((e) => e.errorType !== "Warning");
    const warnings = allItems.filter((e) => e.errorType === "Warning");
    setValidationErrors(errors);
    setValidationWarnings(warnings);
    setShowValidationErrors(!response.isValid || warnings.length > 0);
    setExpandedValidationErrors(!response.isValid);
    if (response.isValid && warnings.length === 0) {
      toast.success(successMessage);
    } else if (response.isValid && warnings.length > 0) {
      toast.success(successMessage);
      toast.warning(`${warnings.length} warning${warnings.length === 1 ? "" : "s"} found.`);
    } else {
      toast.warning(
        `${errorPrefix} found ${errors.length} error${errors.length === 1 ? "" : "s"}.`
      );
    }
  };

  // Save & Validate. Returns whether the edits reached the server, so Approve and Override
  // Approve can persist staged edits before they finalize instead of approving stale data.
  const saveAndValidateEvents = async (): Promise<boolean> => {
    let eventsToSave = tripEvents;
    const activeEvents = eventsToSave.filter((e) => e.isActive !== false);
    const eventsWithoutType = activeEvents.filter((e) => !e.typeId);

    if (eventsWithoutType.length > 0) {
      const entryNumbers = eventsWithoutType.map((e) => activeEvents.indexOf(e) + 1).join(", ");
      toast.error(`Cannot save: Event type is required for entry ${entryNumbers}`);
      return false;
    }

    setIsLoading(true);
    try {
      // Suggested events are persisted in a separate table; /events/merge does not toggle them.
      // Sync any staged deactivations to the suggested-events endpoint before merging so removed
      // suggestions don't reappear on the timeline reload.
      const suggestedDeactivations = eventsToSave
        .filter((e) => e.isSuggested && e.suggestedEventId && e.isActive === false)
        .map((e) => SuggestedEventsService.setSuggestedEventActive(e.suggestedEventId, false));
      if (suggestedDeactivations.length > 0) {
        await Promise.all(suggestedDeactivations);
      }

      const response = await EventsService.saveAndValidate(trip.id, eventsToSave);
      handleValidationResponse(
        response,
        "Events saved and validated successfully.",
        "Events validation"
      );
      // Clear the dirty flag before reload so the timeline-sync effect runs.
      setHasUnsavedChanges(false);
      setTripEvents([]);
      if (activeFilter === "inactive") {
        setActiveFilter("active");
      } else {
        // Await the reload so Save stays disabled until the timeline is back from the server.
        // Without the await, a rapid second click could re-send stale negative-ID suggestions
        // and produce duplicate Event rows (see trips 6667881 / 6668166 on 2026-05-20).
        await dispatch(
          getTimeline({
            tripNumber: trip.id.toString(),
            isActive: activeFilter,
            isReload: true,
          })
        );
      }
      return true;
    } catch (err: any) {
      console.error("[SAVE-ERROR]", err?.response?.status, err?.response?.data, err?.message, err);
      toast.error("Events save failed. Please try again.");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Both approve buttons used to post nothing but the trip id, so an edit still staged in the
   * grid was never sent -- the server then validated and approved the OLD events, the screen
   * reloaded from the database, and the user's change vanished behind a success toast with
   * nothing to say it had been dropped (trip 6783597, 2026-09-24: two events shared a timestamp,
   * the second was moved a minute later, and prod history recorded no time change at all).
   *
   * Clicking Approve with an edit pending means "approve this, including what I just changed",
   * so the edit is persisted first and the approve runs against the saved data. A save that
   * never reached the server stops the approve -- approving stale events is the bug, not the fix.
   */
  const persistStagedEditsBeforeApproval = async (): Promise<boolean> => {
    // Unconditional on purpose. Keying this off hasUnsavedChanges was wrong: Event Reset
    // rebuilds the middle of the trip as GPS SUGGESTIONS and reloads the timeline, which
    // clears the dirty flag, so the grid shows a full trip while the database holds only
    // Begin Trip and End Trip. Approving there approves those two and discards the rest.
    // Suggestions only become Event rows when the merge runs, so approve always runs it --
    // Approve means "save what I am looking at, then approve it", the same as pressing
    // Save and Validate first.
    const saved = await saveAndValidateEvents();
    if (!saved) {
      toast.error("Your event changes could not be saved, so the trip was not approved.");
    }
    return saved;
  };

  // Approve
  const approveTrip = async () => {
    if (!(await persistStagedEditsBeforeApproval())) return;

    setApproveLoading(true);
    tripService
      .finalize(trip.id)
      .then(async (response) => {
        if (response && response.errors) {
          handleValidationResponse(response, "Trip finalized successfully.", "Trip finalization");
        } else {
          toast.success("Trip approval successfully.");
        }
        await dispatch(getTrip(trip.id));
        await runValidation();
      })
      .catch(() => toast.error("Trip approval failed."))
      .finally(() => setApproveLoading(false));
  };

  // Override finalize. Saves first for the same reason Approve does -- and it matters more here:
  // the override waves through validation errors the server computed from the DATABASE, so
  // overriding before the save means overriding errors the edit may have just fixed, and
  // HoldForLitmusOverrideIfApplicableAsync places a B2B hold on the strength of them.
  const overrideFinalizeTrip = async () => {
    if (!(await persistStagedEditsBeforeApproval())) return;

    setApproveLoading(true);
    tripService
      .overrideFinalize(trip.id, overrideReason)
      .then(async (response) => {
        if (response && response.errors) {
          handleValidationResponse(
            response,
            "Trip finalized with override successfully.",
            "Trip override finalization"
          );
        } else {
          toast.success("Trip override approval successfully.");
        }
        await dispatch(getTrip(trip.id));
        await runValidation();
      })
      .catch(() => toast.error("Trip override approval failed."))
      .finally(() => setApproveLoading(false));
  };

  // Reopen
  const reopenTrip = () => {
    setApproveLoading(true);
    tripService
      .reopen(trip.id)
      .then(async () => {
        toast.success("Trip re-opened successfully.");
        await dispatch(getTrip(trip.id));
        await runValidation();
      })
      .catch(() => toast.error("Trip re-open failed."))
      .finally(() => setApproveLoading(false));
  };

  // Deactivate all
  const deactivateAllEvents = () => {
    const updatedEvents = tripEvents.map((event) => ({ ...event, isActive: false }));
    setTripEvents(updatedEvents);
    toast.info("All events deactivated");
  };

  // Verify
  const verifyTrip = async (verified: boolean) => {
    if (!trip?.id) return;
    setVerifyLoading(true);
    try {
      await tripService.verify(trip.id, verified);
      toast.success(verified ? "Trip verified." : "Trip unverified.");
      await dispatch(getTrip(trip.id));
    } catch {
      toast.error("Verify trip failed.");
    } finally {
      setVerifyLoading(false);
    }
  };

  // Manager review
  const setManagerReview = async (mapped: boolean) => {
    if (!trip?.id) return;
    setManagerReviewLoading(true);
    try {
      await tripService.setManagerReview(trip.id, mapped);
      toast.success(mapped ? "Manager review set." : "Manager review cleared.");
      await dispatch(getTrip(trip.id));
    } catch {
      toast.error("Manager review update failed.");
    } finally {
      setManagerReviewLoading(false);
    }
  };

  // Export
  const handleExport = () => {
    if (!trip?.id) return;
    setExportLoading(true);
    EventsService.getTripEventsExport(trip.id, _timeZone)
      .then((data: any) => {
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Trip_${trip.id}_Events.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch(() => toast.error("Failed to export events"))
      .finally(() => setExportLoading(false));
  };

  // Toggle event active
  const handleToggleEventActive = async (event: any, isActive: boolean) => {
    setTripEvents((prev) => prev.filter((e: any) => e.id !== event.id));
    try {
      if (event.isSuggested) {
        await SuggestedEventsService.setSuggestedEventActive(event.suggestedEventId, isActive);
      } else if (event.eventId > 0) {
        await EventsService.setEventActive(event.eventId, isActive);
      }
    } catch (error) {
      console.error("Failed to toggle event active:", error);
      toast.error("Failed to update event");
      dispatch(
        getTimeline({
          tripNumber: trip.id.toString(),
          isActive: activeFilter,
          isReload: true,
        })
      );
    }
  };

  // Drag-and-drop reorder from activity cards. Stage locally; user commits via Save and Validate.
  const handleEventsReorder = (reorderedEvents: any[]) => {
    if (!trip?.id) return;
    setTripEvents(reorderedEvents);
    setHasUnsavedChanges(true);
  };

  // Go to Map: fly the map to the event instead of opening a popup
  const handleGoToMap = (eventId: number) => {
    setSelectedEventId(eventId);
  };

  // Match an event in tripEvents against one returned from the activity cards. `id` is reassigned
  // to the combined activity+timeline position by VehicleHistory, so it cannot be relied on here —
  // suggestedEventId/eventId are server-stable, and _localId is the temp id stamped on unsaved
  // inserts that survives the id-remap.
  const matchesStagedEvent = (a: any, b: any): boolean => {
    if (a.suggestedEventId != null && b.suggestedEventId != null) {
      return a.suggestedEventId === b.suggestedEventId;
    }
    if (a.eventId != null && b.eventId != null) {
      return a.eventId === b.eventId;
    }
    if (a._localId != null && b._localId != null) {
      return a._localId === b._localId;
    }
    return false;
  };

  // Inline event save from activity cards context menu. Stage locally; user commits via Save and Validate.
  const handleInlineEventSave = (updatedEvent: any) => {
    if (!trip?.id) return;

    const allEvents = [...tripEvents];
    const idx = allEvents.findIndex((e) => matchesStagedEvent(e, updatedEvent));

    if (idx >= 0) {
      allEvents[idx] = { ...allEvents[idx], ...updatedEvent };
    } else {
      // New insert - find insertion position based on time
      const insertIdx = allEvents.findIndex(
        (e) => e.time && updatedEvent.time && new Date(e.time) > new Date(updatedEvent.time)
      );
      if (insertIdx >= 0) {
        allEvents.splice(insertIdx, 0, updatedEvent);
      } else {
        allEvents.push(updatedEvent);
      }
    }

    setTripEvents(allEvents);
    setHasUnsavedChanges(true);
  };

  // Inline event remove from activity cards context menu. Stage locally; user commits via Save and Validate.
  const handleInlineEventRemove = (event: any) => {
    setTripEvents((prev) =>
      prev.map((e: any) => (matchesStagedEvent(e, event) ? { ...e, isActive: false } : e))
    );
    setHasUnsavedChanges(true);
  };

  const closerPanel = editMode ? (
    <Box sx={{ height: "100%", overflow: "auto" }}>
      <CloserEventsGrid
        tripId={trip?.id}
        isClosed={trip?.isClosed}
        readOnly={(!trip?.isClosed && !hasAllowApprove) || !!trip?.isApproved}
        tripEvents={tripEvents}
        setTripEvents={setTripEvents}
        eventTypes={eventTypes}
        waitReasons={waitReasons}
        pointsOfService={_pointsOfService}
        timeZone={_timeZone}
        validationErrors={validationErrors}
        validationWarnings={validationWarnings}
        showValidationErrors={showValidationErrors}
        setShowValidationErrors={setShowValidationErrors}
        expandedValidationErrors={expandedValidationErrors}
        setExpandedValidationErrors={setExpandedValidationErrors}
        isLoading={isLoading || timelineLoading}
        approveLoading={approveLoading}
        onApprove={approveTrip}
        onSaveAndValidate={saveAndValidateEvents}
        gridCollapsed={gridCollapsed}
        setGridCollapsed={setGridCollapsed}
        activeFilter={activeFilter}
        setActiveFilter={setActiveFilter}
        canOverride={canOverride}
        overrideReason={overrideReason}
        onOverrideFinalize={overrideFinalizeTrip}
        onDeactivateAll={deactivateAllEvents}
        validationLoaded={validationLoaded}
        hasAllowApprove={hasAllowApprove}
        hasAllowOverride={hasAllowOverride}
        hasAllowClearAllEvents={hasAllowClearAllEvents}
        isApproved={trip?.isApproved}
        onReopen={reopenTrip}
        onExport={handleExport}
        exportLoading={exportLoading}
        onGoToMap={handleGoToMap}
        mapDataReady={mapDataReady}
        isVerified={trip?.tripReview?.isVerified}
        onVerify={verifyTrip}
        verifyLoading={verifyLoading}
        isMapped={trip?.tripReview?.isMapped}
        onSetManagerReview={setManagerReview}
        managerReviewLoading={managerReviewLoading}
        onToggleEventActive={handleToggleEventActive}
        acceptedWaitCount={acceptedWaitCount}
        acceptedWaitMinutes={acceptedWaitMinutes}
        dismissedWaitCount={dismissedWaitCount}
        dismissedWaitMinutes={dismissedWaitMinutes}
      />
    </Box>
  ) : undefined;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mb: 0.5,
        }}
      >
        <Stack direction="row" spacing={1}>
          {hasUnsavedChanges && (
            <Tooltip title="Persist staged event edits and run validation">
              <Button
                variant="contained"
                size="small"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={saveAndValidateEvents}
                disabled={isLoading}
              >
                Save and Validate
              </Button>
            </Tooltip>
          )}
          <Tooltip title={editMode ? "Switch to view mode" : "Switch to full closer grid"}>
            <Button
              variant="contained"
              size="small"
              color={editMode ? "primary" : "inherit"}
              startIcon={editMode ? <ViewIcon /> : <EditIcon />}
              onClick={() => setEditMode(!editMode)}
              sx={{
                backgroundColor: editMode ? undefined : "#fff",
                color: editMode ? undefined : "text.primary",
                border: editMode ? undefined : "1px solid",
                borderColor: editMode ? undefined : "grey.400",
              }}
            >
              {editMode ? "View Mode" : "Full Edit"}
            </Button>
          </Tooltip>
        </Stack>
      </Box>
      <VehicleHistory
        tripNumber={tripNumber}
        isHidden={isHidden}
        sourceIdPrefix="map-tab"
        selectedEventId={selectedEventId ?? undefined}
        onEventSelected={(id) => setSelectedEventId(id)}
        rightPanelContent={closerPanel}
        editable={!editMode}
        eventTypes={eventTypes}
        waitReasons={waitReasons}
        pointsOfService={_pointsOfService}
        onEventSave={handleInlineEventSave}
        onEventRemove={handleInlineEventRemove}
        onEventsReorder={handleEventsReorder}
        // While there are unsaved staged edits, use them as the timeline source so
        // reorders/inline edits show up immediately in the activity cards.
        tripEventsOverride={hasUnsavedChanges ? visibleTripEvents : undefined}
      />
    </Box>
  );
};

export default MapEditLayout;
